import { Trans, useLingui } from "@lingui/react/macro";
import { Button, Field, FieldLabel, Input, Textarea } from "@rakazo/ui-web";
import { Disclosure } from "@rakazo/ui-web/components/ui/disclosure";
import { useId, useState } from "react";
import { McpServerHealthLine } from "../components/McpServerHealthLine";
import { connectMcpOauth } from "../lib/mcp-connect";
import { errorText } from "../lib/user-error";
import { drokRpc } from "./rpc";

export type EmployeeMcpConnection = Awaited<
  ReturnType<typeof drokRpc.copies.mcpConnections>
>[number];

/** Only the field names supplied by IT are editable; stored values are never returned. */
function CredentialFields({
  label,
  keys,
  values,
  onChange,
  multiline = false,
}: {
  label: string;
  keys: string[];
  values: Record<string, string>;
  onChange: (values: Record<string, string>) => void;
  multiline?: boolean;
}) {
  const id = useId();
  if (keys.length === 0) return null;
  return (
    <fieldset className="space-y-2">
      <legend className="text-sm font-medium">{label}</legend>
      {keys.map((key, index) => {
        const inputId = `${id}-${index}`;
        const props = {
          id: inputId,
          value: values[key] ?? "",
          autoComplete: "new-password",
          onChange: (event: { target: { value: string } }) =>
            onChange({ ...values, [key]: event.target.value }),
        };
        return (
          <Field key={key}>
            <FieldLabel htmlFor={inputId}>{key}</FieldLabel>
            {multiline ? <Textarea {...props} /> : <Input {...props} type="password" />}
          </Field>
        );
      })}
    </fieldset>
  );
}

function supplied(values: Record<string, string>): Record<string, string> | undefined {
  const entries = Object.entries(values).filter(([, value]) => value !== "");
  return entries.length ? Object.fromEntries(entries) : undefined;
}

function EmployeeMcpConnectionRow({
  server,
  onChanged,
}: {
  server: EmployeeMcpConnection;
  onChanged: () => Promise<unknown>;
}) {
  const { t } = useLingui();
  const id = useId();
  const [editing, setEditing] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [secret, setSecret] = useState("");
  const [headers, setHeaders] = useState<Record<string, string>>({});
  const [env, setEnv] = useState<Record<string, string>>({});
  const [files, setFiles] = useState<Record<string, string>>({});
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [scope, setScope] = useState("");
  const [authorizationParams, setAuthorizationParams] = useState("");
  const [authorizationServer, setAuthorizationServer] = useState("");
  const shared = server.authMode === "shared";

  const clearInputs = () => {
    setSecret("");
    setHeaders({});
    setEnv({});
    setFiles({});
    setClientId("");
    setClientSecret("");
    setScope("");
    setAuthorizationParams("");
    setAuthorizationServer("");
  };
  const credentials = {
    serverId: server.serverId,
    secret: secret || undefined,
    headers: supplied(headers),
    env: supplied(env),
    files: supplied(files),
    oauthClient: clientId.trim()
      ? {
          clientId: clientId.trim(),
          clientSecret: clientSecret || undefined,
          scope: scope.trim() || undefined,
          authorizationParams: authorizationParams.trim() || undefined,
          authorizationServer:
            server.transport === "stdio" ? authorizationServer.trim() || undefined : undefined,
        }
      : undefined,
  };
  const hasChanges = Boolean(
    credentials.secret ||
      credentials.headers ||
      credentials.env ||
      credentials.files ||
      credentials.oauthClient,
  );

  async function run(action: () => Promise<unknown>) {
    setPending(true);
    setError(null);
    try {
      await action();
      await onChanged();
    } catch (cause) {
      setError(errorText(cause));
    } finally {
      setPending(false);
    }
  }

  return (
    <li className="py-3" data-testid="employee-mcp-connection" data-mode={server.authMode}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="min-w-0 truncate text-sm font-medium">{server.name}</span>
        {shared ? (
          <span className="text-xs text-muted-foreground">
            <Trans>Company credentials</Trans>
          </span>
        ) : (
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={pending}
              onClick={() =>
                void run(async () => {
                  const result = await connectMcpOauth(server.serverId);
                  if (result === "authorization_not_requested") {
                    setError(t`This server did not request browser authorization.`);
                  }
                })
              }
            >
              <Trans>Connect OAuth</Trans>
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={pending}
              aria-expanded={editing}
              aria-controls={`${id}-credentials`}
              onClick={() => {
                clearInputs();
                setError(null);
                setEditing(!editing);
              }}
            >
              <Trans>Credentials</Trans>
            </Button>
          </div>
        )}
      </div>
      <McpServerHealthLine health={server.health} />
      {!shared && server.hasSecret ? (
        <div className="mt-2 flex items-center justify-between gap-2">
          <span className="text-xs text-muted-foreground">
            <Trans>Credential saved</Trans>
          </span>
          <Button
            variant="ghost"
            size="sm"
            disabled={pending}
            onClick={() =>
              void run(async () => {
                await drokRpc.copies.setMcpCredentials({
                  serverId: server.serverId,
                  clearCredential: true,
                });
                clearInputs();
                setEditing(false);
              })
            }
          >
            <Trans>Clear credentials</Trans>
          </Button>
        </div>
      ) : null}
      {!shared && editing ? (
        <form
          id={`${id}-credentials`}
          className="mt-3 space-y-3 rounded-lg border border-border p-3"
          onSubmit={(event) => {
            event.preventDefault();
            if (!hasChanges || pending) return;
            void run(async () => {
              await drokRpc.copies.setMcpCredentials(credentials);
              clearInputs();
              setEditing(false);
            });
          }}
        >
          <Field>
            <FieldLabel htmlFor={`${id}-secret`}>
              <Trans>Access token (optional)</Trans>
            </FieldLabel>
            <Input
              id={`${id}-secret`}
              type="password"
              autoComplete="new-password"
              value={secret}
              onChange={(event) => setSecret(event.target.value)}
              placeholder={t`Stored encrypted`}
            />
          </Field>
          <CredentialFields
            label={t`Headers`}
            keys={server.headerKeys}
            values={headers}
            onChange={setHeaders}
          />
          <CredentialFields
            label={t`Environment variables`}
            keys={server.envKeys}
            values={env}
            onChange={setEnv}
          />
          <CredentialFields
            label={t`Credential files`}
            keys={server.fileKeys}
            values={files}
            onChange={setFiles}
            multiline
          />
          <Disclosure summary={<Trans>OAuth client</Trans>} summaryClassName="text-sm">
            <div className="mt-3 space-y-3">
              <Field>
                <FieldLabel htmlFor={`${id}-client-id`}>
                  <Trans>OAuth client ID (optional)</Trans>
                </FieldLabel>
                <Input
                  id={`${id}-client-id`}
                  value={clientId}
                  autoComplete="off"
                  onChange={(event) => setClientId(event.target.value)}
                />
              </Field>
              {clientId.trim() ? (
                <>
                  <Field>
                    <FieldLabel htmlFor={`${id}-client-secret`}>
                      <Trans>OAuth client secret (optional)</Trans>
                    </FieldLabel>
                    <Input
                      id={`${id}-client-secret`}
                      type="password"
                      autoComplete="new-password"
                      value={clientSecret}
                      onChange={(event) => setClientSecret(event.target.value)}
                    />
                  </Field>
                  {server.transport === "stdio" ? (
                    <Field>
                      <FieldLabel htmlFor={`${id}-authorization-server`}>
                        <Trans>Authorization server</Trans>
                      </FieldLabel>
                      <Input
                        id={`${id}-authorization-server`}
                        type="url"
                        required
                        value={authorizationServer}
                        onChange={(event) => setAuthorizationServer(event.target.value)}
                      />
                    </Field>
                  ) : null}
                  <Field>
                    <FieldLabel htmlFor={`${id}-scope`}>
                      <Trans>Scopes (optional)</Trans>
                    </FieldLabel>
                    <Input
                      id={`${id}-scope`}
                      value={scope}
                      onChange={(event) => setScope(event.target.value)}
                    />
                  </Field>
                  <Field>
                    <FieldLabel htmlFor={`${id}-params`}>
                      <Trans>Authorization parameters (optional)</Trans>
                    </FieldLabel>
                    <Input
                      id={`${id}-params`}
                      value={authorizationParams}
                      onChange={(event) => setAuthorizationParams(event.target.value)}
                    />
                  </Field>
                </>
              ) : null}
            </div>
          </Disclosure>
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="ghost"
              disabled={pending}
              onClick={() => {
                clearInputs();
                setEditing(false);
              }}
            >
              <Trans>Cancel</Trans>
            </Button>
            <Button type="submit" disabled={!hasChanges || pending}>
              {pending ? <Trans>Saving…</Trans> : <Trans>Save</Trans>}
            </Button>
          </div>
        </form>
      ) : null}
      {error ? (
        <p role="alert" className="mt-2 text-sm text-destructive">
          {error}
        </p>
      ) : null}
    </li>
  );
}

export function EmployeeMcpConnections({
  connections,
  onChanged,
}: {
  connections: EmployeeMcpConnection[];
  onChanged: () => Promise<unknown>;
}) {
  if (connections.length === 0) return null;
  return (
    <section className="mt-4" data-testid="employee-mcp-connections">
      <h3 className="text-sm font-medium">
        <Trans>MCP connections</Trans>
      </h3>
      <ul className="divide-y divide-border">
        {connections.map((server) => (
          <EmployeeMcpConnectionRow
            key={`${server.serverId}:${server.authMode}`}
            server={server}
            onChanged={onChanged}
          />
        ))}
      </ul>
    </section>
  );
}
